/*
 * This file is part of the Atlanterhavskomiteen Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  type ComponentProps,
  forwardRef,
  useRef,
  useImperativeHandle,
  useEffect,
} from 'react';
import useNav from 'effects/useNav';
import Icon from 'components/Icons/Content/Content';
import Menu from './Menu/Menu';
import './Nav.scss';

const DEFAULT_LABELS: NavLabels = {
  menu: 'Menu',
  open: 'Open',
  close: 'Close',
};

const DEFAULT_ON_CLICK = () => {};

interface NavLabels {
  menu: string;
  open: string;
  close: string;
}

interface NavProps extends Omit<
  ComponentProps<typeof Menu>,
  'ref' | 'id' | 'controller' | 'active' | 'onClose'
> {
  labels?: NavLabels;
}

export interface NavHandle {
  node: HTMLElement | null;
  open: () => void;
  close: () => void;
}

/**
 * This is the Menu component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */

const Nav = forwardRef<NavHandle, NavProps>(
  ({ labels = DEFAULT_LABELS, onClick = DEFAULT_ON_CLICK, ...props }, ref) => {
    const navRef = useRef<HTMLElement>(null);
    const [buttonNode, menuNode, open, setOpen] = useNav<
      HTMLButtonElement,
      HTMLElement
    >('nav');

    useImperativeHandle(ref, () => ({
      node: navRef.current,
      open: () => {
        setOpen(true);
      },
      close: () => {
        setOpen(false);
      },
    }));

    useEffect(() => {
      if (open) {
        document.body.classList.add('menu-open');
      }

      return function cleanup() {
        document.body.classList.remove('menu-open');
      };
    }, [open]);

    return (
      <>
        <nav ref={navRef} className="nav">
          <button
            ref={buttonNode}
            id="menubutton"
            className="toggle"
            aria-label={open ? labels.close : labels.open}
            aria-haspopup="true"
            aria-controls="menu"
            aria-expanded={open ? 'true' : 'false'}
          >
            <span>
              <Icon title={labels.menu} />
              <span className="text">{labels.menu}</span>
            </span>
          </button>
        </nav>
        <Menu
          ref={menuNode}
          id="menu"
          controller="menubutton"
          {...props}
          onClick={onClick}
          active={open}
          onClose={() => {
            setOpen(false);
          }}
        />
      </>
    );
  }
);

export default Nav;
